import { Card, Checkbox, Col, Descriptions, Form, InputNumber, Modal, Radio, Row, Select, Spin, Table, Typography } from "antd";
import Input from "antd/es/input/Input";
import { validateEsic, validateUAN } from "components/common/validation";
import MyButton from "components/ui/Button/MyButton";
import { getCustomConfiguration } from "hooks/api/configurationApi/payrollPeriodApi";
import { getEmployeeById } from "hooks/api/employeeApi/employeeInformationApi";
import { getEmployeeGroup } from "hooks/api/masterApi/employeeGroupApi";
import { useEffect, useState } from "react";
import { Pie } from "react-chartjs-2";

const { Title, Text } = Typography;
const { Option } = Select;

const calculateAmountFromPercentage = (percentage, base) => {
    if (!percentage || !base) return 0;
    return parseFloat(((percentage / 100) * base).toFixed(2));
};

const regimeType = [
    { label: "Old Regime", value: 'Old Regime' },
    { label: "New Regime", value: 'New Regime' },
];

const init = {
    annual_ctc: 0,
    monthly_ctc: 0,
    earnings: [],
    deductions: [],
    total_earning: {
        percentage: 0,
        monthly_amount: null,
        annual_amount: null
    },
    total_deduction: {
        percentage: 0,
        employee_monthly_amount: null,
        employer_monthly_amount: null,
        annual_amount: 0
    },
    net_salary: 0,
    gross_salary: 0,
    total_deductions_percentage: 0,
    total_deductions_monthly_amount: 0,
    total_deductions_annual_amount: 0,
    net_salary_monthly: 0,
    salary_id: 0,
    is_active: 0,
    salary_template_id: 0,
    gross_monthly_amount: 0,
    gross_annual_amount: 0,
    regime_type: "New Regime"
}

const NewSalaryInfoBasedOnTemplate = ({ formData, salaryTemplateList, actualTemplateData, OnSave, OnBack, updateWholeFormData, isScrLoading, isLoading, docInfo, setDocInfo, docOnSave, getdocInfo, employeeId }) => {

    const [templates, setTemplates] = useState([]);
    const [MonthlyCTC, setMonthlyCTC] = useState(null);
    const [YearlyCTC, setYearlyCTC] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [earnings, setEarnings] = useState([]);
    const [deductions, setDeductions] = useState([])
    const [isPANorESCI, setIsPANorESCI] = useState(false)
    const [uanNumber, setUanNumber] = useState("");
    const [esicNumber, setEsicNumber] = useState("");
    const [isEsicOptional, setIsEsicOptional] = useState(false);
    const [docFormData, setDocFormData] = useState(docInfo)
    const [eGroup, setEGroup] = useState([])
    const [currentEmpDetails, setCurrentEmpDetails] = useState({})
    const [initialFormData, setInitialFormData] = useState(init)

    const [form] = Form.useForm();
    const customConfig = JSON.parse(sessionStorage.getItem("customConfig"));
    const is_super_admin = JSON.parse(sessionStorage.getItem("is_super_admin"));
    const is_admin = JSON.parse(sessionStorage.getItem("is_admin"));
    const esicEmployeeLimit = 21000;
    const salary_calculation = "gross";
    let isEditAccess = (is_super_admin || is_admin);

    // Fetch templates on mount
    useEffect(() => {
        if (salaryTemplateList) {
            const combinedTemplates = (Object.values(salaryTemplateList) || []).flat();
            setTemplates(combinedTemplates);
        }
    }, [salaryTemplateList]);

    // employee group and employee api
    const employeeGroup = async () => {
        setEGroup([])
        try {
            const apiData = await getEmployeeGroup(`drop_down=True`);
            if (apiData && apiData.status === "success" && apiData.data) {
                setEGroup(apiData.data?.length > 0 ? [...apiData.data] : [])
            } else {
                setEGroup([])
            }
        } catch (error) {
            setEGroup([])
        }
    };

    const employeeDetails = async () => {
        setCurrentEmpDetails({})
        try {
            const apiData = await getEmployeeById(employeeId);
            if (apiData && apiData.status === "success" && apiData.data && apiData?.data?.result?.length > 0) {
                setCurrentEmpDetails({ ...apiData.data.result[0] })
            }
        } catch (error) {
            console.error(error)
        }
    }

    useEffect(() => {
        employeeGroup();
        employeeDetails();
    }, []);

    const getEmployeeGroupDetails = (employee, employeeGroups) => {
        if (!employee?.professional_info?.employee_group || !Array.isArray(employeeGroups)) return null;

        const groupId = employee.professional_info.employee_group;

        // Find the group that matches the employee's group ID
        const groupDetails = employeeGroups.find(group => group.id === groupId);

        return groupDetails || null;
    };

    const employeeGroupInfo = getEmployeeGroupDetails(currentEmpDetails, eGroup);

    // Set initial form data
    useEffect(() => {
        if (formData) {
            form.setFieldsValue({
                ...formData,
                salary_template_id: formData.salary_template_id
            });

            setMonthlyCTC(formData?.monthly_ctc || null);
            setYearlyCTC(formData?.monthly_ctc ? formData.monthly_ctc * 12 : null);

            if (formData?.salary_template_id) {
                handleTemplateChange(formData.salary_template_id);
            }

            if (formData.earnings) {
                setEarnings([...formData.earnings]);
            }

            if (formData.deductions) {
                setDeductions([...formData.deductions]);
            }

            setInitialFormData({
                ...initialFormData,
                is_active: formData.is_active,
                salary_id: formData.salary_id,
            })
        }
    }, [formData]);

    // Auto-update yearly when monthly changes
    useEffect(() => {
        if (MonthlyCTC != null) {
            const yearly = parseFloat(MonthlyCTC) * 12;
            setYearlyCTC(yearly.toFixed(2));
        }
    }, [MonthlyCTC]);

    const handleTemplateChange = (value) => {
        const template = templates.find((t) => t.id === value); // find the selected template using id
        if (template) {
            setSelectedTemplate(template);
            form.setFieldsValue(template);
        }
    };

    const handleMonthlyChange = (value) => {
        setMonthlyCTC(value);
    };

    const handleYearlyChange = (value) => {
        setYearlyCTC(value);
        setMonthlyCTC(value ? (value / 12).toFixed(2) : null);
    };

    const earningComponentAmounts = () => {
        if (!MonthlyCTC || !selectedTemplate) return {};

        let component = {};

        // First, find Basic amount (since others may depend on it)
        const basicComponent = selectedTemplate.earning_components.find(e => e.name.toLowerCase() === 'basic');
        if (basicComponent) {
            component['Basic'] = calculateAmountFromPercentage(basicComponent.percentage, MonthlyCTC);
        }

        // Now calculate all components
        selectedTemplate.earning_components.forEach(item => {
            let baseAmount = 0;

            if (item.calcof?.toLowerCase() === 'ctc') {
                baseAmount = MonthlyCTC;
            } else if (item.calcof?.toLowerCase() === 'basic') {
                baseAmount = component['Basic'] || 0;
            } else {
                // fallback: use CTC
                baseAmount = MonthlyCTC;
            }

            component[item.name] = calculateAmountFromPercentage(item.percentage, baseAmount);
        });

        return component;
    };

    // console.log('earningComponentAmounts', earningComponentAmounts());

    const deductionComponentAmounts = () => {
        if (!MonthlyCTC || !selectedTemplate) return {};

        const isDeduction = customConfig?.is_deduction === false;
        const isContractAndNoDeduction =
            employeeGroupInfo?.is_contract_employee === true &&
            customConfig?.deduct_for_contract_emp === false;

        let component = {};
        const earnings = earningComponentAmounts(); // cache this so we don’t re-run multiple times

        selectedTemplate.deduction_components.forEach((item) => {
            const { name, employee_share_percentage, is_amount, calcof } = item;
            if (isDeduction || isContractAndNoDeduction) return;

            // Determine base amount based on calcof
            let baseAmount = 0;
            if (calcof?.toLowerCase() === "ctc") {
                baseAmount = MonthlyCTC;
            } else if (earnings[calcof]) {
                baseAmount = earnings[calcof]; // if calcof matches "Basic", "DA", etc.
            } else {
                baseAmount = MonthlyCTC; // fallback
            }

            // If is_amount is true, then use the amount directly instead of % calculation
            if (is_amount) {
                component[name] = {
                    employeeAmount: item.employee_share_monthly_amount || 0,
                    percentage: null
                };
                return;
            }

            // Special case: PF
            if (name === "PF") {
                const basicPay = earnings.Basic || 0;
                const dearnessAllowance = earnings.DA || 0;
                const totalConsideredAmount = basicPay + dearnessAllowance;
                const pfLimit = customConfig.pf_limit || 0;
                const pfCap = customConfig.cap_amount || 0;

                let employeePf = 0;
                if (!employee_share_percentage || employee_share_percentage === 0) {
                    employeePf = 0;
                } else if (totalConsideredAmount > pfLimit) {
                    employeePf = pfCap;
                } else {
                    employeePf = calculateAmountFromPercentage(
                        employee_share_percentage,
                        totalConsideredAmount
                    );
                }

                component[name] = {
                    employeeAmount: employeePf,
                    percentage: employee_share_percentage
                };
                return;
            }

            // Special case: ESIC
            if (name === "ESIC") {
                const esicLimit = esicEmployeeLimit;
                const employeeEsic =
                    esicLimit >= MonthlyCTC
                        ? calculateAmountFromPercentage(employee_share_percentage, MonthlyCTC)
                        : 0;

                component[name] = {
                    employeeAmount: employeeEsic,
                    percentage: employee_share_percentage
                };
                return;
            }

            // Default case for other deductions
            component[name] = {
                employeeAmount: calculateAmountFromPercentage(employee_share_percentage, baseAmount),
                percentage: employee_share_percentage
            };
        });

        return component;
    };

    // console.log('deductionComponentAmounts', deductionComponentAmounts());

    const employerContributionAmounts = () => {
        if (!MonthlyCTC || !selectedTemplate) return {};

        const isDeduction = customConfig?.is_deduction === false;
        const isContractAndNoDeduction =
            employeeGroupInfo?.is_contract_employee === true &&
            customConfig?.deduct_for_contract_emp === false;

        const component = {};
        const earnings = earningComponentAmounts(); // cache earnings so we don’t recalculate

        selectedTemplate.deduction_components.forEach((item) => {
            const { is_amount, name, employer_share_percentage, monthly_amount, calcof } = item;

            if (isDeduction || isContractAndNoDeduction) return;

            // If fixed amount mode, skip percentage calculation
            if (is_amount) {
                component[name] = {
                    employerAmount: item.employer_share_monthly_amount || 0,
                    percentage: null
                };
                return;
            }

            // Determine calculation base from calcof
            let baseAmount = 0;
            if (calcof?.toLowerCase() === "ctc") {
                baseAmount = MonthlyCTC;
            } else if (earnings[calcof]) {
                baseAmount = earnings[calcof]; // If "Basic" or "DA" or any other earning component
            } else {
                baseAmount = MonthlyCTC; // fallback
            }

            // PF logic
            if (name === "PF") {
                const basicPay = earnings.Basic || 0;
                const dearnessAllowance = earnings.DA || 0;
                const totalConsideredAmount = basicPay + dearnessAllowance;
                const pfLimit = customConfig.pf_limit || 0;
                const pfCap = customConfig.cap_amount || 0;

                let employerPf = 0;
                if (!employer_share_percentage || employer_share_percentage === 0) {
                    employerPf = 0;
                } else if (totalConsideredAmount > pfLimit) {
                    employerPf = pfCap;
                } else {
                    employerPf = calculateAmountFromPercentage(employer_share_percentage, totalConsideredAmount);
                }

                component[name] = {
                    employerAmount: employerPf,
                    percentage: employer_share_percentage
                };
                return;
            }

            // ESIC logic
            if (name === "ESIC") {
                const esicLimit = esicEmployeeLimit;
                const employerEsic =
                    esicLimit >= MonthlyCTC
                        ? calculateAmountFromPercentage(employer_share_percentage, MonthlyCTC)
                        : 0;

                component[name] = {
                    employerAmount: employerEsic,
                    percentage: employer_share_percentage
                };
                return;
            }

            // Default case for other deductions
            component[name] = {
                employerAmount: calculateAmountFromPercentage(employer_share_percentage, baseAmount),
                percentage: employer_share_percentage
            };
        });

        return component;
    };

    // console.log('employerContributionAmounts', employerContributionAmounts());

    useEffect(() => {
        const calcualtedEarnings = earningComponentAmounts(); // dynamic values

        // Step 1: Get all earning keys (merge existing + calculated)
        const allEarningKeys = new Set([
            ...earnings.map((e) => e.name),
            ...Object.keys(calcualtedEarnings)
        ]);

        // Step 2: Create a map from current earnings array
        const existingMap = earnings.reduce((acc, item) => {
            acc[item.name] = item;
            return acc;
        }, {});

        // Step 3: Build full array using all keys
        const updated = Array.from(allEarningKeys).map((key) => {
            const monthly = calcualtedEarnings[key] || 0;
            // const percentage = MonthlyCTC
            //     ? parseFloat(((monthly / MonthlyCTC) * 100).toFixed(2))
            //     : 0;

            // Take original percentage from selectedTemplate
            const original = selectedTemplate?.earning_components.find(e => e.name === key);
            const percentage = original?.percentage || 0;
            // const templateEntry = selectedTemplate?.earning_components.find(e => e.name === key);

            return {
                name: key,
                ...existingMap[key], // keep other existing props
                monthly_amount: monthly,
                annual_amount: monthly * 12,
                percentage: percentage,
                calcof: original?.calcof || existingMap[key]?.calcof || null
            };
        });
        setEarnings(updated);
    }, [MonthlyCTC, selectedTemplate]);

    // console.log('earnings', earnings);

    useEffect(() => {
        const employeeShare = deductionComponentAmounts(); // for employee side
        const employerShare = employerContributionAmounts(); // for employer side

        // Step 1: Get all unique deduction names from both employee & employer
        const allDeductionKeys = new Set([
            ...Object.keys(employeeShare),
            ...Object.keys(employerShare),
        ]);

        // Step 2: Create a map from existing deductions by name
        const existingMap = deductions.reduce((acc, item) => {
            acc[item.name] = item;
            return acc;
        }, {});

        // Step 3: Build the final updated deduction array
        const updated = Array.from(allDeductionKeys).map((key) => {
            const employeeMonthlyAmount = employeeShare[key]?.employeeAmount || 0;
            const employeeMonthlyPercent = employeeShare[key]?.percentage || 0;
            const employerMonthlyAmount = employerShare[key]?.employerAmount || 0;
            const employerMonthlyPercent = employerShare[key]?.percentage || 0;

            const annual_amount = (employeeMonthlyAmount + employerMonthlyAmount) * 12;

            return {
                name: key,
                ...existingMap[key], // preserves other custom fields from original `deductions`
                employee_share_monthly_amount: employeeMonthlyAmount,
                employee_share_annual_amount: employeeMonthlyAmount * 12,
                employee_share_percentage: employeeMonthlyPercent,
                employer_share_monthly_amount: employerMonthlyAmount,
                employer_share_annual_amount: employerMonthlyAmount * 12,
                employer_share_percentage: employerMonthlyPercent,
                annual_amount: annual_amount
            };
        });

        setDeductions(updated);
    }, [MonthlyCTC, selectedTemplate]);


    // console.log('deductions', deductions);


    useEffect(() => {
        // const totalMonthlyEarnings = earnings.reduce((acc, curr) => acc + curr.monthly_amount, 0);
        const allowedCalcof = ["CTC"]; // or ["CTC", "Basic"]
        const totalMonthlyEarnings = earnings.reduce((acc, curr) => {
            return allowedCalcof.includes(curr.calcof) 
                ? acc + (curr.monthly_amount || 0) 
                : acc;
        }, 0);
        
        const totalAnnualEarnings = totalMonthlyEarnings * 12;

        const totalEmployeeDeductionMonthly = deductions.reduce((acc, curr) => acc + curr.employee_share_monthly_amount, 0);
        const totalEmployerDeductionMonthly = deductions.reduce((acc, curr) => acc + curr.employer_share_monthly_amount, 0);        
        const totalDeductionsMonthly = totalEmployeeDeductionMonthly + totalEmployerDeductionMonthly;
        const totalDeductionsAnnual = totalDeductionsMonthly * 12;

        const netMonthly = totalMonthlyEarnings - totalDeductionsMonthly;
        const netAnnual = netMonthly * 12;        

        const totalMonthlyCTC = totalMonthlyEarnings;
        const totalAnnualCTC = totalMonthlyCTC * 12;

        const totalDeductionPercentage = totalMonthlyEarnings
            ? (totalDeductionsMonthly / totalMonthlyEarnings) * 100
            : 0;

        const employeeDeductionPercent = totalMonthlyEarnings
            ? (totalEmployeeDeductionMonthly / totalMonthlyEarnings) * 100
            : 0;

        const finalPayload = {
            ...initialFormData,
            annual_ctc: totalAnnualCTC,
            monthly_ctc: totalMonthlyCTC,
            earnings: earnings,
            deductions: deductions,
            total_earning: {
                percentage: 100,
                monthly_amount: totalMonthlyEarnings,
                annual_amount: totalAnnualEarnings
            },
            total_deduction: {
                percentage: employeeDeductionPercent,
                employee_monthly_amount: totalEmployeeDeductionMonthly,
                employer_monthly_amount: totalEmployerDeductionMonthly,
                annual_amount: totalDeductionsAnnual
            },
            total_deductions_percentage: totalDeductionPercentage,
            total_deductions_monthly_amount: totalDeductionsMonthly,
            total_deductions_annual_amount: totalDeductionsAnnual,
            net_salary: netMonthly,
            net_salary_monthly: netMonthly,
            gross_salary: totalAnnualEarnings - (totalEmployeeDeductionMonthly * 12),
            gross_monthly_amount: totalMonthlyEarnings - totalEmployeeDeductionMonthly,
            gross_annual_amount: totalAnnualEarnings - (totalEmployeeDeductionMonthly * 12),
            salary_template_id: selectedTemplate?.id || 0
        };

        console.log('finalPayload', finalPayload);
        
        setInitialFormData(finalPayload);
        getdocInfo(employeeId).then(res => {
            setDocFormData(res)
            setDocInfo(res)
        });
    }, [earnings, deductions, MonthlyCTC, selectedTemplate]);

    useEffect(() => {
        if (docFormData?.employee_document) {
            const pf = docFormData.employee_document?.uan || "";
            const esic = docFormData.employee_document?.esic_no || "";

            setUanNumber(pf);
            setEsicNumber(esic);

            form.setFieldsValue({
                pfNumber: pf,
                esicNumber: esic,
            });
        }
    }, [docFormData, form, isPANorESCI]);

    // console.log('initialFormData', initialFormData);

    const onChange = (stateKey, value) => {
        setInitialFormData((prevState) => ({
            ...prevState,
            [stateKey]: value
        }))
    }

    const renderRadioButton = (key, list) => {
        return (
            <Radio.Group
                className="custom-radio-group ml-3"
                // disabled = {!userPermission?.edit}
                onChange={(e) => onChange(key, e.target.value)} value={initialFormData[key]}>
                {list?.map(item => {
                    return (
                        <Radio value={item?.value} key={item?.value}><span className=" font-medium text-xs">{item?.label}</span></Radio>
                    )
                })}
            </Radio.Group>
        )
    }

    // submit function
    const onFinish = () => {
        if (salary_calculation === "gross") {
            // console.log('Data while submit', initialFormData);

            let updated = {
                ...initialFormData,
                annual_ctc: employeeGroupInfo?.is_contract_employee === true ? initialFormData.gross_monthly_amount * 12 : initialFormData.annual_ctc,
                monthly_ctc: employeeGroupInfo?.is_contract_employee === true ? initialFormData.gross_monthly_amount : initialFormData.monthly_ctc,
                net_salary: employeeGroupInfo?.is_contract_employee === true ? initialFormData.gross_monthly_amount : initialFormData.net_salary,
                net_salary_monthly: employeeGroupInfo?.is_contract_employee === true ? initialFormData.gross_monthly_amount : initialFormData.net_salary_monthly,
                deductions: employeeGroupInfo?.is_contract_employee === true && customConfig?.deduct_for_contract_emp === false ? [] : initialFormData.deductions,
                total_deductions_monthly_amount: employeeGroupInfo?.is_contract_employee === true ? 0 : initialFormData.total_deductions_monthly_amount,
            };

            // Step 1: Check if PF or ESIC exists in deductions
            let hasPForESIC = updated.deductions.some(item =>
                item.name.includes('PF') || item.name.includes('ESIC')
            );

            // Step 2: If PF or ESIC exists, then check if UAN or ESIC number is missing
            if (hasPForESIC) {
                const { uan, esic_no } = docFormData.employee_document;
                const isUanMissing = !uan;
                const isEsicMissing = !esic_no && !isEsicOptional;

                if (isUanMissing || isEsicMissing) {
                    setIsPANorESCI(true);
                    return
                }
            }

            OnSave(updated);

            console.log('updated', updated);
            
            console.log('initialFormData', initialFormData);
            
            updateWholeFormData("salary_info", initialFormData);
        } else if (salary_calculation === "ctc") {
            // ...
        }
    }

    // table columns
    const earningColumns = [
        { title: "Description", dataIndex: "description" },
        { title: "Percentage", dataIndex: "percentage" },
        { title: "Monthly Amount", dataIndex: "monthlyAmount" },
        { title: "Annual Amount", dataIndex: "yearlyAmount" },
    ];

    const deductionColumns = [
        { title: "Description", dataIndex: "description" },
        { title: "Percentage", dataIndex: "percentage" },
        { title: "Monthly Amount", dataIndex: "monthlyAmount" },
        { title: "Annual Amount", dataIndex: "yearlyAmount" },
    ];

    const earningAmounts = earningComponentAmounts();

    const earningDataSource = Object.keys(earningAmounts).map((key, index) => {
        const component = selectedTemplate.earning_components.find(
            (item) => item.name === key
        );

        return {
            key: index,
            description: key.toUpperCase(),
            percentage: component ? `${component.percentage}%` : '-',
            monthlyAmount: earningAmounts[key],
            yearlyAmount: earningAmounts[key] * 12
        };
    });

    const deductionAmounts = deductionComponentAmounts();

    const deductionDataSource = Object.keys(deductionAmounts).map((key, index) => {
        const component = deductionAmounts[key]; // { employeeAmount, percentage }

        return {
            key: index,
            description: key.toUpperCase(),
            percentage: component.percentage !== null ? `${component.percentage}%`: 'Fixed',
            monthlyAmount: component.employeeAmount,
            yearlyAmount: component.employeeAmount * 12
        };
    });

    const earningsData = earningComponentAmounts();
    const deductionsData = deductionComponentAmounts();
    const contributionData = employerContributionAmounts()
    const hasData = Object.keys(earningsData).length > 0 || Object.keys(deductionsData).length > 0;

    const chartData = {
        labels: hasData
            ? [
                ...Object.keys(earningsData).map((key) => key.toUpperCase()),
                ...Object.keys(deductionsData).map((key) => key.toUpperCase())
            ]
            : ["No Data"],

        datasets: [
            {
                label: "Salary Components",
                data: hasData
                    ? [
                        ...Object.values(earningsData),
                        ...Object.values(deductionsData).map((item) => item.employeeAmount)
                    ]
                    : [0],

                backgroundColor: [
                    "#36A2EB", "#FF6384", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40",
                    "#8BC34A", "#F44336", "#00BCD4", "#FFEB3B", "#E91E63", "#3F51B5"
                ],
            },
        ],
    };

    const grossSalary = earnings.reduce((acc, curr) => {
        if (curr.calcof !== "CTC") {
            return acc; // skip if not based on CTC
        }
        return acc + (curr.monthly_amount || 0);
    }, 0);

    const totalDeduction = Object.values(deductionsData).reduce(
        (acc, val) => acc + (val.employeeAmount || 0),
        0
    );
    const totalEmployerContribution = Object.values(contributionData).reduce(
        (acc, val) => acc + (val.employerAmount || 0),
        0
    );
    const netPay = grossSalary - totalDeduction - totalEmployerContribution;
    const monthlyCTCIncludingPF = grossSalary;
    const yearlyCTCIncludingPF = monthlyCTCIncludingPF * 12;

    return (
        <>
            {isScrLoading && (
                <div className="loaderOverlay">
                    <Spin />
                </div>
            )}
            <Card>
                <div style={{ display: "flex", gap: 20, marginTop: "5px", overflowY: 'scroll', height: '68vh', alignItems: "stretch" }}>
                    <div style={{ width: '100%' }}>
                        <div className="flex flex-col gap-1">
                            <div className="flex justify-between items-center">
                                <Title level={4}>Salary Structure</Title>
                                <Form form={form}>
                                    <Form.Item label='Select Salary Template' name='salary_template_id'>
                                        <Select placeholder='Select Template' style={{ width: '18vw' }} onChange={handleTemplateChange}>
                                            {templates.map((item) => (
                                                <Option key={item.id} value={item.id}>
                                                    {item.salary_template_name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Form>
                            </div>

                            <Form layout="vertical" form={form}>
                                <Row gutter={10} className="flex justify-between mt-2 mb-2">
                                    <Col span={10} className="flex items-center gap-2">
                                        <p className='salaryTemp_tlt'>Monthly CTC <span className='requiredTxt'>*</span></p>
                                        <Form.Item rules={[{ required: true, message: "This field is required" }]}>
                                            <InputNumber
                                                disabled={!isEditAccess}
                                                value={MonthlyCTC}
                                                onChange={handleMonthlyChange}
                                                style={{ width: "100%", fontSize: "13px", textAlign: "right", border: '1px solid #d9d9d9', borderRadius: '6px' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={10} className="flex items-center gap-2">
                                        <p className='salaryTemp_tlt'>Yearly CTC <span className='requiredTxt'>*</span></p>
                                        <Form.Item rules={[{ required: true, message: "This field is required" }]}>
                                            <InputNumber
                                                disabled={!isEditAccess}
                                                value={YearlyCTC}
                                                onChange={handleYearlyChange}
                                                style={{ width: "100%", fontSize: "13px", textAlign: "right", border: '1px solid #d9d9d9', borderRadius: '6px' }}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>

                            {/* eanrings table */}
                            <Title level={5}>Earnings</Title>
                            <Table columns={earningColumns} dataSource={earningDataSource} pagination={false} />

                            {/* Deductions Section */}
                            <div className="flex gap-1 items-center">
                                <Title level={5} style={{ marginTop: "16px" }}>Deductions</Title>
                            </div>

                            {customConfig?.is_deduction === false ? (
                                //Case 1: Global Deductions turned off 
                                <p className="mt-2 text-[10px] text-gray-600 font-medium">
                                    Deductions are not applicable as per company policy. You will receive your full gross pay.
                                </p>
                            ) : employeeGroupInfo?.is_contract_employee === true &&
                                customConfig?.deduct_for_contract_emp === false ? (
                                // Case 2: Contract employee but no deductions
                                <p className="mt-2 text-[10px] text-gray-600 font-medium">
                                    Deductions are not applicable for contract employees as per company policy. You will receive your full gross pay.
                                </p>
                            ) : (
                                // Case 3: Deductions are applicable
                                <>
                                    <Table
                                        columns={deductionColumns}
                                        dataSource={deductionDataSource}
                                        pagination={false}
                                    />
                                </>
                            )}
                        </div>
                        {/* <div className=" flex justify-between mt-3">
                            <span className='salaryTemp_tlt'>TDS</span>
                            <span className="salaryTemp_tlt"> Regime Type : {renderRadioButton("regime_type", regimeType)}</span>
                        </div> */}
                        <div style={{ marginTop: "10px", padding: "10px", background: "#fafafa", borderRadius: "8px", border: "0.5px solid #d9d9d9" }}>
                            <Typography.Text strong style={{ fontSize: "12px", color: "#555" }}>
                                Note:
                            </Typography.Text>
                            <ul style={{ margin: "5px 0px", paddingLeft: "20px", fontSize: "8px", color: "#777" }}>
                                <li>Net Salary excludes employer contributions.</li>
                                <li>ESIC will apply only if the monthly gross salary is ₹{esicEmployeeLimit} or below.</li>
                                <li>PF is calculated based on the Basic up to ₹{customConfig?.pf_limit}.</li>
                            </ul>
                        </div>
                    </div>
                    <div style={{ width: '30%' }}>
                        <Pie data={chartData} style={{ width: 280 }} />
                        <br />
                        <Descriptions bordered column={1} layout="vertical" size="small" className=" mb-5">
                            <Descriptions.Item label="Net Pay Salary">
                                <Text strong>₹{netPay.toFixed(2)}</Text>
                            </Descriptions.Item>
                            {/* <Descriptions.Item label="Monthly CTC Including PF, ESCI,.. (Shares)">
                                <Text strong>₹{monthlyCTCIncludingPF.toFixed(2)}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Yearly CTC Including PF, ESCI,.. (Shares)">
                                <Text strong>₹{yearlyCTCIncludingPF.toFixed(2)}</Text>
                            </Descriptions.Item> */}
                            <Descriptions.Item label="Total Deduction from Your CTC">
                                <Text strong>₹{totalDeduction.toFixed(2)}</Text>
                            </Descriptions.Item>
                            <Descriptions.Item label="Total Contribution from Employer">
                                {Object.keys(contributionData || {}).length > 0 ? (
                                    Object.keys(contributionData).map((key) => (
                                        <div key={key}>
                                            <Text strong>{key}</Text> ({contributionData[key]?.percentage || 0}%) : ₹{(contributionData[key]?.employerAmount || 0).toFixed(2)}
                                        </div>
                                    ))
                                ) : (
                                    <Text strong>₹0.00</Text>
                                )}
                            </Descriptions.Item>
                        </Descriptions>
                    </div>
                </div>
            </Card>
            <Card className=" flex justify-center items-center mt-1">
                <MyButton htmlType="button" label="Back" outlined={true} onClick={() => OnBack("fromSalary")} paddingX={"0 2vw"} marginRight={"0.625vw"} />
                <MyButton htmlType="submit" label={"Next"} loading={isLoading} onClick={onFinish} paddingX={"0 2vw"} />
            </Card>

            <Modal
                centered
                width={"30vw"}
                open={isPANorESCI}
                onCancel={() => setIsPANorESCI(false)}
                footer={
                    <MyButton
                        type="primary"
                        label="OK"
                        onClick={async () => {
                            try {
                                const values = await form.validateFields();

                                const updatedDocData = {
                                    ...docFormData,
                                    employee_document: {
                                        ...docFormData.employee_document,
                                        uan: uanNumber,
                                        esic_no: isEsicOptional ? "" : esicNumber,
                                    },
                                };

                                docOnSave(updatedDocData);

                                setDocFormData(updatedDocData);
                                setDocInfo(updatedDocData);

                                // Refresh from backend
                                const freshDoc = await getdocInfo(employeeId);
                                setDocFormData(freshDoc);
                                setDocInfo(freshDoc);

                                // Reset
                                form.resetFields();
                                setUanNumber("");
                                setEsicNumber("");
                                setIsPANorESCI(false);
                            } catch (err) {
                                console.error("Validation Failed:", err);
                            }
                        }}

                    />
                }
            >
                <div>
                    <p>Please provide PAN/ESIC details.</p>
                    <Form form={form} style={{ marginTop: '0.8rem' }}>
                        <Form.Item
                            name="pfNumber"
                            rules={[
                                {
                                    required: true,
                                    message: "PF Number is required",
                                },
                                { validator: validateUAN },
                            ]}
                        >
                            <Input
                                placeholder="PF Number (Every Pf number starts with one)"
                                maxLength={12}
                                value={uanNumber}
                                onChange={(e) => setUanNumber(e.target.value)}
                            />
                        </Form.Item>

                        <Form.Item
                            name="esicNumber"
                            rules={[
                                {
                                    required: isEsicOptional ? false : true,
                                    message: "ESIC Number is required",
                                },
                                { validator: validateEsic },
                            ]}
                        >
                            <Input
                                placeholder="ESIC Number"
                                maxLength={17}
                                value={esicNumber}
                                onChange={(e) => setEsicNumber(e.target.value)}
                                disabled={isEsicOptional}
                            />
                        </Form.Item>
                        <Form.Item>
                            <Checkbox
                                checked={isEsicOptional}
                                onChange={(e) => {
                                    setIsEsicOptional(e.target.checked);
                                    // Clear any validation error if making it optional
                                    if (e.target.checked) {
                                        form.setFields([
                                            {
                                                name: "esicNumber",
                                                errors: [],
                                            },
                                        ]);
                                    }
                                }}
                            >
                                Make ESIC Number Optional
                            </Checkbox>
                        </Form.Item>

                    </Form>
                </div>
            </Modal>

        </>
    );
};

export default NewSalaryInfoBasedOnTemplate;
